<template src="./formOrdine.html">
</template>

<script>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { get_opzioni } from '@/api/api.parametri.js'

export default {
    name: 'FormOrdine',

    props: {
        ordine: {
            type: Object,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        customerId: {
            type: Number,
            default: null
        }
    },

    emits: ['salva'],

    setup(props, { emit }) {
        const formRef = ref(null)
        const datiInterni = ref({ ...props.ordine })

        // Stato salvataggio automatico
        const autoSalvatoIn = ref(null)   // 'saving' | 'saved' | 'error' | null
        let debounceTimer = null
        let isFirstLoad = true

        // Tutte le sezioni aperte di default
        const panelAperti = ref([0, 1, 2, 3, 4, 5, 6])

        // Opzioni select - inizialmente vuote, caricate da API
        const opzioni = ref({
            essenze: [],
            serie: [],
            profili: [],
            spessori: [],
            verniciature: [],
            persiane: [],
            avvolgibili: [],
            antoine: [],
            zanzariereTipo: [],
            zanzariereColore: [],
            tipi: [],
        })

        // Flag di caricamento
        const loadingOpzioni = ref(true)

        /**
         * Carica le opzioni dal backend
         */
        const caricaOpzioni = async () => {
            try {
                loadingOpzioni.value = true
                const response = await get_opzioni(props.customerId)

                // Converto da oggetto con chiavi PascalCase a camelCase
                opzioni.value = {
                    essenze: response.data.essenze || [],
                    serie: response.data.serie || [],
                    profili: response.data.profili || [],
                    spessori: response.data.spessori || [],
                    verniciature: response.data.verniciature || [],
                    persiane: response.data.persiane || [],
                    avvolgibili: response.data.avvolgibili || [],
                    antoine: response.data.antoine || [],
                    zanzariereTipo: response.data.zanzariereTipo || [],
                    zanzariereColore: response.data.zanzariereColore || [],
                    tipi: response.data.tipi || [],
                }
            } catch (error) {
                console.error('Errore nel caricamento delle opzioni:', error)
                // In caso di errore, mantengo gli array vuoti
            } finally {
                loadingOpzioni.value = false
            }
        }

        // Carico le opzioni al mount del componente
        onMounted(async () => {
            await caricaOpzioni()
            // Dopo il caricamento iniziale, abilito il watch autosave
            setTimeout(() => { isFirstLoad = false }, 100)
        })

        onUnmounted(() => {
            clearTimeout(debounceTimer)
        })

        const isEmpty = (s) => !s || !String(s).trim()

        const rules = {
            required: value => !!value || 'Campo obbligatorio',

            // OK se ragioneSociale presente OPPURE se entrambi nome+cognome presenti
            personaGiuridicaOFisica_RS: () => {
                const haRS = !isEmpty(datiInterni.value.ragioneSociale)
                const haPersona =
                    !isEmpty(datiInterni.value.nomeCliente) &&
                    !isEmpty(datiInterni.value.cognomeCliente)

                return (haRS || haPersona) || 'Inserisci ragione sociale oppure nome + cognome'
            },

            personaGiuridicaOFisica_COG: (v) => {
                const haRS = !isEmpty(datiInterni.value.ragioneSociale)
                if (haRS) return true

                const hasCognome = !isEmpty(v)
                const hasNome = !isEmpty(datiInterni.value.nomeCliente)
                return (hasCognome && hasNome) || 'Campo obbligatorio'
            },

            // Se manca la ragione sociale, richiede NOME + COGNOME
            personaGiuridicaOFisica_NOME: (v) => {
                const haRS = !isEmpty(datiInterni.value.ragioneSociale)
                if (haRS) return true

                const hasNome = !isEmpty(v)
                const hasCognome = !isEmpty(datiInterni.value.cognomeCliente)
                return (hasNome && hasCognome) || 'Campo obbligatorio'
            }
        }

        watch(() => props.ordine, (newVal) => {
            datiInterni.value = { ...newVal }
            if (datiInterni.value.data) {
                datiInterni.value.data = datiInterni.value.data.split('T')[0]
            }
            if (datiInterni.value.dataConsegna) {
                datiInterni.value.dataConsegna = datiInterni.value.dataConsegna.split('T')[0]
            }
        }, { deep: true })

        // Watch su customerId - ricarica opzioni se cambia
        watch(() => props.customerId, () => {
            caricaOpzioni()
        })

        // Watch per salvataggio automatico su datiInterni (solo form ordine, non tabella misure)
        watch(() => datiInterni, async () => {
            // Ignora il primo caricamento iniziale
            if (isFirstLoad) return

            clearTimeout(debounceTimer)
            autoSalvatoIn.value = null

            debounceTimer = setTimeout(async () => {
                const { valid } = await formRef.value.validate()
                if (!valid) return

                autoSalvatoIn.value = 'saving'
                try {
                    const dati = { ...datiInterni.value }
                    if (dati.verniciatura?.toLowerCase() !== 'bicolore') {
                        dati.bicoloreInt = ''
                        dati.bicoloreEst = ''
                    }
                    emit('salva', dati)
                    autoSalvatoIn.value = 'saved'
                    setTimeout(() => { autoSalvatoIn.value = null }, 3000)
                } catch {
                    autoSalvatoIn.value = 'error'
                }
            }, 1000)
        }, { deep: true })

        const salva = async () => {
            const { valid } = await formRef.value.validate()
            if (valid) {
                if (datiInterni.value.verniciatura.toLowerCase() != 'bicolore') {
                    datiInterni.value.bicoloreInt = "";
                    datiInterni.value.bicoloreEst = "";
                }
                emit('salva', datiInterni.value)
            }
        }

        return {
            formRef,
            datiInterni,
            panelAperti,
            opzioni,
            loadingOpzioni,
            rules,
            salva,
            autoSalvatoIn,
        }
    }
}
</script>

<style scoped>
/* ─── Card contenitore ─── */
.form-ordine {
    margin: 0 auto;
}

/* ─── Header della form ─── */
.form-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    background: #fafafa;
    border-radius: 8px 8px 0 0;
    position: sticky;
    top: 0;
    z-index: 2;
}

/* ─── Titoli delle sezioni accordion ─── */
.section-title {
    min-height: 40px !important;
    font-size: 0.82rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    background: #f7f7f8;
    transition: background 0.2s ease;
}

.section-title:hover {
    background: #eef0f3;
}

/* ─── Contenuto delle panel: padding ridotto ─── */
.v-expansion-panel-text__wrapper {
    padding: 8px 12px 14px !important;
}

/* ─── Input: altezza più compatta ─── */
.v-text-field input,
.v-select .v-input__slot {
    font-size: 0.875rem;
}

/* ─── Checkbox: allineamento compatto ─── */
.v-checkbox {
    margin-top: 0 !important;
}

/* ─── Row dense: gap minimo tra campi ─── */
.v-row--dense {
    margin: 0 !important;
}

/* ─── Transizione indicatore autosave ─── */
.fade-autosave-enter-active,
.fade-autosave-leave-active {
    transition: opacity 0.3s ease;
}

.fade-autosave-enter-from,
.fade-autosave-leave-to {
    opacity: 0;
}
</style>